import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import {
  CHART_RADIO_BUTTON_TRACKING_TITLE,
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import {
  FactRevenue,
  FeatureOption,
  FilterKeys,
  Filters,
  getPageTypeWithPageName,
  ShareType,
} from '@utils/pageConfig/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { WithHiddenListBox } from '@components/design/WithHiddenListBox';
import { SortDirection, SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { IRootState, RetailerClientStateKeys } from '@state/reducers/Reducers';
import {
  constructCurrencyLabel,
  formatLabelIntoRevenuePriceTitle,
  isRevenueType,
} from '@utils/currencyUtils/CurrencyUtils';
import {
  BUTTON_MENU_CONFIG,
  getKpiOptions,
  getSortOptions,
  removeDuplicateSegByFilterOption,
  SelectorType,
} from '@utils/design/ButtonMenuUtils';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnConfig, SortDirectionType, SortFieldDisplayProps } from '@components/design/migrated/Table';
import { FeatureSelectorType } from '@components/feature-selector/Types';
import {
  ColumnHeaderForPA,
  getDisplayNameFromHeaderKey,
  getSortDirectionLabel,
  SortKeys,
  SortLabels,
} from '@models/PerformanceAnalysisboard';
import { RetailerKpiOption, RetailerKpiOptionDisplayName, SaleType } from '@models/Products';
import { buildOptions, CATEGORY_FEATURE_OPTION } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { multiCountryDisabledFilters } from '@utils/multiCountryUtils/MultiCountryUtils';
import { ExportTabView, ViewType } from '@hooks/export/Model';
import { ShareTypeOptions } from '@const/PerformanceType';
import { getHeaderKey } from '@data-visualization/utilities/ChartUtils';
import { updateShareTypeAndSortingKeyCL } from '@components/category-leaderboard/category-leaderboard-table-columns/CategoryLeaderboardTableColumns';
import { Kpi } from '@utils/Formatters';
import { KpiTypeTrendStackChart } from '@pages/explore/ExploreContants';

type ShareTypeOption = {
  label: string;
  value: ShareType;
};

type SaleTypeOption = {
  label: string;
  value: SaleType;
};

type AbsoluteTypeOption = {
  label: string;
  value: ShareType;
};

export interface ListBoxProps {
  isDisabled: boolean;
  chartName: string;
  getDisplayName: (headerKey: string, volume: string) => string;
  retailerClientStateKey?: RetailerClientStateKeys;
  factRevenue: FactRevenue;
  sorting: SortingOptions;
  kpisConfig: ColumnConfig[];
  stackedChartSorting?: SortingOptions;
  updateSorting: (options: SortingOptions) => void;
  updateStackChartSorting?: (options: SortingOptions) => void;
  featureFilters: Filters[];
  segmentByFeature?: FeatureOption;
  setSegmentByFeature?: (f: FeatureOption) => void;
  showPerformanceWithin: FeatureOption;
  featureSelectorType: FeatureSelectorType;
  featureHandler?: () => void;
  onKpiSelectHandler?: () => void;
  onSortHandler?: () => void;
  segmentByHandler?: (feat: FeatureOption) => void;
  volume: string;
  sortByStackedChart?: SortFieldDisplayProps;
  setSortByStackedChart?: (sortByStackedChart: SortFieldDisplayProps) => void;
  isStackedChart: boolean;
  tabView: ExportTabView;
  viewType: ViewType;
  selectedShareType?: ShareType;
  setSelectedShareType?: Dispatch<SetStateAction<ShareType>>;
  selectedSaleType?: SaleType;
  setSelectedSaleType?: Dispatch<SetStateAction<SaleType>>;
  selectedAbsoluteType?: ShareType;
  setSelectedAbsoluteType?: Dispatch<SetStateAction<ShareType>>;
  defaultSaleTypeOptions?: SaleTypeOption[] | KpiTypeTrendStackChart[];
  selectedKpi?: string;
}

const ListBoxSelector: React.FC<ListBoxProps> = ({
  isDisabled,
  chartName,
  getDisplayName,
  retailerClientStateKey = 'retailerClient',
  factRevenue,
  sorting,
  kpisConfig,
  stackedChartSorting = {
    headerKey: '',
    sortKey: '',
    sortDir: SortDirection.asc,
  },
  updateSorting,
  updateStackChartSorting = () => {},
  featureFilters,
  segmentByFeature = CATEGORY_FEATURE_OPTION,
  setSegmentByFeature = () => {},
  showPerformanceWithin,
  featureSelectorType,
  featureHandler,
  onKpiSelectHandler,
  onSortHandler,
  segmentByHandler,
  volume,
  sortByStackedChart = {
    label: '',
    sortKey: '',
    sortDirection: SortDirection.asc,
  },
  setSortByStackedChart = () => {},
  isStackedChart,
  tabView,
  viewType,
  selectedShareType,
  setSelectedShareType,
  selectedSaleType = SaleType.revenue,
  setSelectedSaleType = () => {},
  selectedAbsoluteType = ShareType.Absolute,
  setSelectedAbsoluteType = () => {},
  defaultSaleTypeOptions,
  selectedKpi,
}) => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);
  const retailerClientState = useSelector((state: IRootState) => state[retailerClientStateKey]);
  const currencyLabel = constructCurrencyLabel(factRevenue);
  const kpiOptions = getKpiOptions(kpisConfig, volume, currencyLabel, getDisplayName).filter((option) => {
    if (chartName === 'Explore Segments Stack Chart') {
      return !['marketExcludeRetailPrice'].includes(option.value);
    }
    return true;
  });
  const sortOptions = getSortOptions(kpisConfig, sorting, isStackedChart);

  const [featureValue, setFeatureValue] = useState<string | null>(featureSelectorType.feature.value);
  const [kpiValue, setKpiValue] = useState<string | null>(sorting.headerKey);
  useEffect(() => {
    if (selectedKpi && selectedKpi != kpiValue) {
      setKpiValue(selectedKpi);
    }
  }, [selectedKpi]);
  const [sortValue, setSortValue] = useState<string | null>(
    isStackedChart
      ? `${sortByStackedChart.sortKey || SortKeys.Absolute}|${sortByStackedChart.label}|${
          sortByStackedChart.sortDirection
        }`
      : `${sorting.sortKey}|${sorting.sortDir}`,
  );
  useEffect(() => {
    if (isStackedChart && sortByStackedChart) {
      setSortValue(
        `${sortByStackedChart.sortKey || SortKeys.Absolute}|${sortByStackedChart.label}|${
          sortByStackedChart.sortDirection
        }`,
      );
    }
  }, [JSON.stringify(sortByStackedChart)]);

  const shareOptions: ShareTypeOption[] = [];

  if (viewType === ViewType.LineChart) {
    shareOptions.push({
      label: RetailerKpiOptionDisplayName[RetailerKpiOption.absolute],
      value: ShareType.Absolute,
    });

    shareOptions.push({
      label: `Retailer ${RetailerKpiOptionDisplayName[RetailerKpiOption.share]}`,
      value: ShareType.MarketShare,
    });

    shareOptions.push({
      label: `Retailer ${RetailerKpiOptionDisplayName[RetailerKpiOption.mix]}`,
      value: ShareType.MyMix,
    });
  } else {
    shareOptions.push({
      label: ShareTypeOptions.MARKET_SHARE,
      value: ShareType.MarketShare,
    });

    shareOptions.push({
      label: ShareTypeOptions.RETAILER_MIX,
      value: ShareType.MyMix,
    });
  }

  const getShareLabel = (share: ShareType): string => {
    const shareOption = shareOptions.find((option) => option.value === share) || shareOptions[0];
    return shareOption.label;
  };

  const saleTypeOptions: SaleTypeOption[] | KpiTypeTrendStackChart[] = defaultSaleTypeOptions ?? [
    { label: `Revenue${currencyLabel}`, value: SaleType.revenue },
    { label: volume, value: SaleType.volume },
    { label: `Price${currencyLabel}`, value: SaleType.price },
  ];

  const absoluteTypeOptions: AbsoluteTypeOption[] = [
    { label: `Absolute`, value: ShareType.Absolute },
    { label: 'Share', value: ShareType.MarketShare },
  ];

  const getSaleTypeLabel = (type: SaleType | KpiTypeTrendStackChart): string => {
    const saleTypeOption = saleTypeOptions.find((option) => option.value === type) || saleTypeOptions[0];
    return saleTypeOption.label;
  };

  const getAbsoluteTypeLabel = (type: ShareType): string => {
    const saleTypeOption = absoluteTypeOptions.find((option) => option.value === type) || absoluteTypeOptions[0];
    return saleTypeOption.label;
  };
  const featureOptions = buildOptions(featureSelectorType.featureFilters);
  const segmentByOptions = buildOptions(removeDuplicateSegByFilterOption(featureFilters, showPerformanceWithin));
  const countryOptions =
    sideFiltersState?.appliedFilters[pagesConfig.pageType].selectedFilters[FilterKeys.COUNTRY]?.options;
  const filteredFeatureOptions = useMemo(
    () =>
      countryOptions?.length === 1
        ? featureOptions
        : featureOptions.filter((x) => !multiCountryDisabledFilters.includes(x.filterKey)),
    [featureOptions, countryOptions, multiCountryDisabledFilters],
  );

  const filteredSegmentByOptions =
    countryOptions?.length === 1
      ? segmentByOptions
      : segmentByOptions.filter((x) => !multiCountryDisabledFilters.includes(x.filterKey));
  const selectedKpiOption = kpiOptions.find((o) => o.value === kpiValue) || kpiOptions[0];
  const selectedSortOption = sortOptions.find((o) => o.value === sortValue) || sortOptions[0];

  const handleChartInteractionTracking = (optionChosen: string, chartElementName: ChartElementName) => {
    //Start Chart Interaction Tracking
    const trackingParam: ChartInteractionTrackingFunctionParam = {
      eventTitle: CHART_RADIO_BUTTON_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      elementName: chartElementName,
      elementType: ChartElementType.FILTER,
      optionChosen,
      chartName,
      access: pagesConfig.access,
    };
    chartInteractionTrackingFunction(trackingParam);
    //End Chart Interaction Tracking
  };

  //sets share type and update and maintain sorting order when switched between mix and share
  const setShareTypeAndUpdateSorting = (value: ShareType) => {
    if (selectedShareType && setSelectedShareType) {
      updateShareTypeAndSortingKeyCL(pagesConfig, setSelectedShareType)(value);
    }
  };
  useEffect(() => {
    setFeatureValue(showPerformanceWithin.value);
  }, [showPerformanceWithin.value]);

  useEffect(() => {
    if (!selectedKpi && sorting?.headerKey !== kpiValue) {
      setKpiValue(sorting.headerKey);
    }
    const updatedSortValue = `${sorting.sortKey}|${sorting.sortDir}`;

    if (!isStackedChart && sortValue !== updatedSortValue) {
      setSortValue(updatedSortValue);
    }
  }, [sorting]);

  const updateShareType = () => {
    if ([Kpi.yourRevenueMix, Kpi.yourVolumeMix].includes(kpiValue as Kpi)) {
      setSelectedShareType?.(ShareType.MyMix);
    } else {
      setSelectedShareType?.(ShareType.MarketShare);
    }
  };

  //Custom requirements for Explore Trends Line Chart
  //If Price sale type is selected, then change Share Typ dropdown to ABSOLUTE. Since it doesnt make sensse price to have share or mix.
  const updateDefaultPriceShareType = (
    saleType: SaleType,
    setSelectedShareType: React.Dispatch<React.SetStateAction<ShareType>> | undefined,
  ) => {
    if (saleType === SaleType.price && setSelectedShareType) {
      setSelectedShareType(ShareType.Absolute);
    }
  };

  useEffect(() => {
    if (!selectedKpiOption || selectedKpiOption.value === sorting.headerKey) {
      return;
    }

    if (isStackedChart) {
      updateStackChartSorting({
        ...stackedChartSorting,
        headerKey: selectedKpiOption.value,
      });
      if (
        ![ColumnHeaderForPA.marketRetailerRevenueShare, ColumnHeaderForPA.marketRetailerVolumeShare].includes(
          selectedKpiOption.value as ColumnHeaderForPA,
        )
      ) {
        // When KPI other than Revenue or Volume Share is selected, it should update back pages config sorting options
        // to allow it to be carried over to other view
        updateSorting({
          ...sorting,
          headerKey: selectedKpiOption.value,
        });
      }
      handleChartInteractionTracking(
        `${formatLabelIntoRevenuePriceTitle(
          getDisplayNameFromHeaderKey(selectedKpiOption.value, volume),
          currencyLabel,
        )}`,
        ChartElementName.FOR,
      );
      return;
    }
    updateShareType();
    updateSorting({
      ...sorting,
      headerKey: selectedKpiOption.value,
    });
    handleChartInteractionTracking(
      isRevenueType(selectedKpiOption.value)
        ? formatLabelIntoRevenuePriceTitle(getDisplayName(selectedKpiOption.value, volume), currencyLabel)
        : getDisplayName(selectedKpiOption.value, volume),
      ChartElementName.WITH,
    );

    onKpiSelectHandler?.();
  }, [kpiValue]);

  useEffect(() => {
    if (isStackedChart) {
      if (
        selectedSortOption &&
        selectedSortOption.value !==
          `${sortByStackedChart.sortKey}|${sortByStackedChart.label}|${sortByStackedChart.sortDirection}`
      ) {
        const [sortKey, sortLabel, sortDir] = selectedSortOption.value.split('|');

        const newSortByStackedChart = {
          sortKey: SortKeys[sortKey as keyof typeof SortKeys],
          label: SortLabels[sortLabel as keyof typeof SortLabels],
          sortDirection: SortDirectionType[sortDir as keyof typeof SortDirectionType],
        };
        setSortByStackedChart(newSortByStackedChart);
        handleChartInteractionTracking(
          `${sortLabel} ${getSortDirectionLabel(
            SortDirectionType[sortDir as keyof typeof SortDirectionType],
            SortKeys[sortKey as keyof typeof SortKeys],
          )}`,
          ChartElementName.BY,
        );
      }
      return;
    }
    const [sortKey, sortDir] = selectedSortOption.value.split('|');
    if (sortKey !== sorting.sortKey || sortDir !== sorting.sortDir) {
      updateSorting({
        ...sorting,
        sortDir: sortDir as SortDirection,
        sortKey,
        label: sortKey || 'Absolute',
      });
      onSortHandler?.();
      handleChartInteractionTracking(selectedSortOption.label, ChartElementName.SORT_BY);
    }
  }, [sortValue]);

  const selectedOption = useMemo(
    () => filteredFeatureOptions.find((o) => o.value === featureValue) ?? filteredFeatureOptions[0] ?? null,
    [featureValue, filteredFeatureOptions],
  );

  const applySelection = useCallback(
    (opt: typeof selectedOption) => {
      if (!opt) return;
      handleChartInteractionTracking(opt.label, ChartElementName.SHOW);
      featureSelectorType.setFeature(opt);
      featureHandler?.();
    },
    [handleChartInteractionTracking, featureSelectorType, featureHandler],
  );

  const lastAppliedRef = useRef<any>(featureSelectorType?.feature?.value ?? null);

  useEffect(() => {
    if (!selectedOption) return;
    if (selectedOption.value === lastAppliedRef.current) return;
    applySelection(selectedOption);
    lastAppliedRef.current = selectedOption.value;
  }, [selectedOption, applySelection]);

  const onChangeSegmentByFeature = (option: FeatureOption) => {
    const optionToUpdate = filteredSegmentByOptions.find((o) => o.value === option.value);
    if (optionToUpdate && optionToUpdate?.feature !== segmentByFeature.value) {
      handleChartInteractionTracking(optionToUpdate.feature, ChartElementName.BY);
      setSegmentByFeature(optionToUpdate);

      if (segmentByHandler) {
        segmentByHandler(optionToUpdate);
      }
    }
  };

  const selectors = BUTTON_MENU_CONFIG[pagesConfig.pageType]?.[tabView]?.[viewType]?.selectors ?? [];
  const kpiLabel = viewType === ViewType.StackedChart ? 'for' : 'with';
  const sortingLabel = viewType === ViewType.StackedChart ? 'by' : 'sorted by';

  return (
    <div className="flex items-center">
      {selectors.map((selector) => {
        switch (selector) {
          case SelectorType.SHOW_PERFORMANCE_WITHIN:
            return (
              <div key={SelectorType.SHOW_PERFORMANCE_WITHIN} data-testid={'show-performance-within-selector'}>
                <WithHiddenListBox
                  isDisabled={isDisabled}
                  label={'Show'}
                  options={filteredFeatureOptions}
                  value={featureValue}
                  onChange={(option) => {
                    setFeatureValue(option.value);
                  }}
                />
              </div>
            );
          case SelectorType.SHARE:
            return (
              selectedShareType && (
                <div key={SelectorType.SHARE} data-testid={'share-selector'}>
                  <WithHiddenListBox
                    isDisabled={
                      viewType === ViewType.LineChart && selectedSaleType === SaleType.price && !isDisabled
                        ? true
                        : isDisabled
                    } //disable if price is selected
                    label={viewType === ViewType.LineChart ? 'and' : 'with'}
                    options={shareOptions}
                    value={getShareLabel(selectedShareType)}
                    onChange={({ value }: ShareTypeOption) => {
                      setShareTypeAndUpdateSorting(value);
                      handleChartInteractionTracking(
                        getShareLabel(value),
                        viewType === ViewType.LineChart ? ChartElementName.AND : ChartElementName.WITH,
                      );
                    }}
                  />
                </div>
              )
            );
          case SelectorType.KPI:
            return (
              <div key={SelectorType.KPI} data-testid={'kpi-selector'}>
                <WithHiddenListBox
                  isDisabled={isDisabled}
                  label={kpiLabel}
                  options={kpiOptions}
                  value={selectedKpiOption?.label}
                  onChange={(option) => {
                    setKpiValue(option.value);
                  }}
                />
              </div>
            );
          case SelectorType.SEGMENT_BY:
            return (
              <div key={SelectorType.SEGMENT_BY} data-testid={'segment-selector'}>
                <WithHiddenListBox
                  isDisabled={isDisabled}
                  label={'by'}
                  options={filteredSegmentByOptions}
                  value={segmentByFeature.value}
                  onChange={onChangeSegmentByFeature}
                />
              </div>
            );
          case SelectorType.SORTED_BY:
            return (
              <div key={SelectorType.SORTED_BY} data-testid={'sortBy-kpi-selector'}>
                <WithHiddenListBox
                  label={sortingLabel}
                  isDisabled={isDisabled}
                  options={sortOptions}
                  value={selectedSortOption.label}
                  onChange={(option) => {
                    setSortValue(option.value);
                  }}
                />
              </div>
            );
          case SelectorType.SALES_TYPE:
            return (
              <div
                key={`${SelectorType.SALES_TYPE}-${saleTypeOptions}`} //NOSONAR typescript:S6551
                data-testid={'salesType-selector'}
              >
                <WithHiddenListBox
                  isDisabled={isDisabled}
                  label={'by'}
                  options={saleTypeOptions}
                  value={getSaleTypeLabel(selectedSaleType)}
                  onChange={({ value }: SaleTypeOption) => {
                    setSelectedSaleType(value);
                    updateDefaultPriceShareType(value, setSelectedShareType);
                    updateSorting({
                      ...sorting,
                      headerKey: getHeaderKey(value),
                    });
                    handleChartInteractionTracking(value, ChartElementName.BY);
                  }}
                />
              </div>
            );
          case SelectorType.ABSOLUTE:
            return (
              <div
                key={`${SelectorType.ABSOLUTE}-${absoluteTypeOptions.length}`} //NOSONAR typescript:S6551
                data-testid={'absolute-selector'}
              >
                <WithHiddenListBox
                  isDisabled={isDisabled}
                  label={'as'}
                  options={absoluteTypeOptions}
                  value={getAbsoluteTypeLabel(selectedAbsoluteType)}
                  onChange={({ value }: ShareTypeOption) => {
                    setSelectedAbsoluteType(value);
                  }}
                />
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default ListBoxSelector;
